import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { UploadFileRounded } from '@mui/icons-material';
import { Box, Stack, TextField, CircularProgress } from '@mui/material';

import { Center } from 'components/Common/Centered';

import * as SourceService from 'services/sourceService';

import { useToaster } from 'contexts/ToasterContext';

import { PopUpButtonProps } from 'types';

import { PopUp } from 'components/PopUp';

interface IAddImagePopUp {
  isOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
}

export const AddImagePopUp = ({ isOpen, close, onSuccess }: IAddImagePopUp) => {
  const [imageName, setImageName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { triggerToast } = useToaster();
  const queryClient = useQueryClient();

  const getPresignedURLFn = async ({
    fileExtension,
    userFileName,
  }: {
    fileExtension: string;
    userFileName: string;
  }) => {
    return await SourceService.getPresignedURLForFaqaMedia(fileExtension, userFileName);
  };

  const { mutate: getPresignedURL, isLoading: isGettingPresignedURL } = useMutation(
    getPresignedURLFn,
    {
      onSuccess: async (data: any) => {
        setIsUploading(true);

        try {
          // Upload the image file
          await SourceService.uploadFile(data.presignedUrl, file);
          setIsUploading(false);
          close();

          triggerToast({
            message: 'Image uploaded successfully',
            type: 'success',
          });

          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          triggerToast({
            message: 'Error occurred while uploading image',
            type: 'error',
          });
          setIsUploading(false);
        }
      },
      onError: (error: any) => {
        setIsUploading(false);
        triggerToast({
          message: error.response?.data?.message
            ? `Error: ${error.response.data.message}`
            : 'Error occurred while uploading the image',
          type: 'error',
        });
      },
    }
  );

  const handleOnDropFile = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      rejectedFiles.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === 'file-too-large') {
            triggerToast({
              message: 'File too large, should not be larger than 5MB',
              type: 'error',
            });
          }

          if (err.code === 'file-invalid-type') {
            triggerToast({
              message: 'File type invalid, please upload an image file',
              type: 'error',
            });
          }
        });
      });

      setFile(null);
      setPreviewUrl(null);

      if (acceptedFiles.length) {
        const acceptedFile = acceptedFiles[0];

        // Start local file loading state
        setIsFileLoading(true);
        setFile(acceptedFile);

        // Auto-fill the image name with the file name (without extension)
        const fileName = acceptedFile.name; //.split('.').slice(0, -1).join('.');
        setImageName(fileName);

        // Generate a local preview to visualize the image and control loader timing
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
          setIsFileLoading(false);
        };
        reader.readAsDataURL(acceptedFile);
      } else {
        setIsFileLoading(false);
      }
    },
    [triggerToast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleOnDropFile,
    multiple: false,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    // 5 MB
    maxSize: 5000000,
  });

  const buttons: PopUpButtonProps[] = [
    {
      name: 'CANCEL',
      handler: () => {
        close();
      },
      variant: 'outlined',
    },
    {
      name: 'UPLOAD',
      handler: () => {
        uploadImage();
      },
      variant: 'contained',
      isLoading: isUploading || isGettingPresignedURL,
    },
  ];

  const uploadImage = () => {
    if (!file || !imageName.trim()) {
      triggerToast({
        message: 'Please provide an image name and select a file',
        type: 'error',
      });
      return;
    }

    const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
    const params = {
      fileExtension: fileExtension,
      userFileName: `${imageName}`,
    };

    getPresignedURL(params);
  };

  useEffect(() => {
    if (isOpen) {
      setImageName('');
      setFile(null);
      setPreviewUrl(null);
      setIsFileLoading(false);
    }
  }, [isOpen]);

  return (
    <PopUp
      isPopUpOpen={isOpen}
      title="ADD IMAGE"
      buttons={buttons}
      close={close}
      maxWidth="sm"
      fullWidth
      disableCTA={!(Boolean(imageName?.trim()) && Boolean(file))}
    >
      {/* Input files */}
      <Box sx={{ mb: '10px' }}></Box>
      {/* Input Field */}
      <Stack direction="column" alignItems="flex-start" width="100%">
        <TextField
          placeholder="Enter Image Name"
          label="Image Name"
          fullWidth
          required
          size="small"
          value={imageName}
          onChange={(e) => setImageName(e.target.value)}
          sx={{ mb: 3 }}
        />
        <Center
          width="100%"
          height="220px"
          border="1px dashed"
          borderColor={isDragActive ? 'primary.main' : 'grey.300'}
          sx={{
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
          borderRadius={1}
          bgcolor="grey.100"
          color="grey.600"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {isFileLoading ? (
            <>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Box component="span">Loading preview...</Box>
            </>
          ) : previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt={imageName || file?.name || 'Selected image'}
              sx={{ maxHeight: '90%', maxWidth: '90%', objectFit: 'contain' }}
            />
          ) : (
            <>
              <UploadFileRounded fontSize="large" sx={{ mb: 1 }} />
              {file ? file.name : 'Upload Image (JPG, PNG, GIF, WEBP)'}
            </>
          )}
        </Center>
      </Stack>
    </PopUp>
  );
};
